import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  Box,
  Button,
  Loader,
  ScrollArea,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getAllProducts } from '@/Api';
import { ProductListPreviewPicture } from '@/components/ProductListPreviewPicture';
import { PRODUCT_CARD_PREVIEW_BG } from '@/constants/productPreview';
import type { CompareSelectionItem } from '@/store/compareStore';
import type { IProduct } from '@/types/Product.model';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import classes from './CompareSlotSearch.module.css';

type CompareSlotSearchProps = {
  accentColor: string;
  selectedIds: string[];
  onAdd: (item: CompareSelectionItem) => void;
  /** Shared catalog so multiple empty slots do not each hit the API. */
  catalog?: IProduct[] | null;
  catalogLoading?: boolean;
  /** True when the shared catalog request failed, so an empty list is not "no results". */
  catalogError?: boolean;
  onRetryCatalog?: () => void;
};

function productSearchHaystack(product: IProduct): string {
  const tags = (product.tags ?? []).join(' ');
  return `${product.productName ?? ''} ${product.articleNumber ?? ''} ${tags}`.toLowerCase();
}

export function CompareSlotSearch({
  accentColor,
  selectedIds,
  onAdd,
  catalog: catalogProp,
  catalogLoading: catalogLoadingProp,
  catalogError: catalogErrorProp,
  onRetryCatalog,
}: CompareSlotSearchProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [localCatalog, setLocalCatalog] = useState<IProduct[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(false);
  const [localAttempt, setLocalAttempt] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const ownsCatalog = catalogProp === undefined;
  const catalog = ownsCatalog ? localCatalog : (catalogProp ?? []);
  const catalogLoading = ownsCatalog ? localLoading : Boolean(catalogLoadingProp);
  const catalogError = ownsCatalog ? localError : Boolean(catalogErrorProp);

  useEffect(() => {
    if (!ownsCatalog || !open || localCatalog.length > 0) return;
    let cancelled = false;
    setLocalLoading(true);
    setLocalError(false);
    getAllProducts()
      .then((products) => {
        if (!cancelled) setLocalCatalog(products.filter((p) => Boolean(p._id)));
      })
      .catch(() => {
        if (!cancelled) {
          setLocalCatalog([]);
          setLocalError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLocalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ownsCatalog, open, localCatalog.length, localAttempt]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? catalog.filter((product) => productSearchHaystack(product).includes(q))
      : catalog;
    return list
      .filter((p) => !selectedIds.includes(p._id))
      .slice(0, 40)
      .sort((a, b) => (a.productName || '').localeCompare(b.productName || ''));
  }, [catalog, query, selectedIds]);

  return (
    <Box
      ref={rootRef}
      className={classes.slot}
      style={{
        borderColor: open ? accentColor : 'var(--mantine-color-default-border)',
        background: open ? colorWithAlpha(accentColor, 0.06) : undefined,
      }}
    >
      <Text size="sm" c="dimmed" mb={8} fw={600}>
        {t('marketplace.compare.slotEmpty')}
      </Text>
      <TextInput
        value={query}
        onChange={(event) => {
          setQuery(event.currentTarget.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={t('marketplace.compare.searchPlaceholder')}
        leftSection={<IconSearch size={16} />}
      />
      {open ? (
        <Box className={classes.dropdown}>
          {catalogLoading ? (
            <Box className={classes.dropdownStatus}>
              <Loader size="sm" color={accentColor} />
              <Text size="sm" c="dimmed" ml="sm">
                {t('marketplace.compare.loading')}
              </Text>
            </Box>
          ) : catalogError ? (
            <Box className={classes.dropdownError}>
              <Text size="sm" c="red">
                {t('marketplace.compare.loadError')}
              </Text>
              <Button
                size="xs"
                variant="light"
                color={accentColor}
                onClick={() => {
                  if (ownsCatalog) setLocalAttempt((attempt) => attempt + 1);
                  else onRetryCatalog?.();
                }}
              >
                {t('marketplace.retry')}
              </Button>
            </Box>
          ) : filtered.length === 0 ? (
            <Text size="sm" c="dimmed" p="sm">
              {t('marketplace.compare.noResults')}
            </Text>
          ) : (
            <ScrollArea h={220} type="auto" offsetScrollbars>
              <div className={classes.list}>
                {filtered.map((product) => (
                  <UnstyledButton
                    key={product._id}
                    className={classes.row}
                    onClick={() => {
                      onAdd({
                        _id: product._id,
                        productName: product.productName,
                        productImage: product.productImage,
                        imageVersion: product.imageVersion,
                        articleNumber: product.articleNumber,
                      });
                      setQuery('');
                      setOpen(false);
                    }}
                  >
                    <Box className={classes.thumb} style={{ background: PRODUCT_CARD_PREVIEW_BG }}>
                      {product.productImage ? (
                        <ProductListPreviewPicture
                          entityId={product._id}
                          productImage={product.productImage}
                          imageVersion={product.imageVersion}
                          alt={product.productName}
                        />
                      ) : null}
                    </Box>
                    <div className={classes.meta}>
                      <Text fw={600} size="sm" lineClamp={1}>
                        {product.productName}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {product.articleNumber || '—'}
                      </Text>
                    </div>
                    <Button
                      size="xs"
                      variant="light"
                      color={accentColor}
                      leftSection={<IconPlus size={14} />}
                      onClick={(event: MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        onAdd({
                          _id: product._id,
                          productName: product.productName,
                          productImage: product.productImage,
                          imageVersion: product.imageVersion,
                          articleNumber: product.articleNumber,
                        });
                        setQuery('');
                        setOpen(false);
                      }}
                    >
                      {t('marketplace.compare.add')}
                    </Button>
                  </UnstyledButton>
                ))}
              </div>
            </ScrollArea>
          )}
        </Box>
      ) : null}
    </Box>
  );
}
